import { apiMedService } from '@/api'
import type {
  BankAccountList,
  BankInfo,
  CompanyListData,
  CompanyParams,
  CompanyParamsListData,
  CompanyUserListData,
  ThirdPartyCompanyListData,
  UserItem,
} from '@/api/models/company'
import type { BaseQueryParams } from '@/types'

export const companyApi = apiMedService.injectEndpoints({
  endpoints: (builder) => ({
    getUsersList: builder.query<
      UserItem[],
      {
        companyId?: string | number
        params: BaseQueryParams
      }
    >({
      query: ({ companyId, params }) => ({
        url: `/v1/user/company/${companyId}/employee`,
        params,
      }),
      transformResponse: (response: CompanyUserListData) =>
        response?.data || [],
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCompanyList: builder.query<CompanyListData, any>({
      query: (params) => ({
        url:
          `/v1/user/company` +
          '/?' +
          `${
            params?.query?.short_title
              ? `short_title=${params?.query?.short_title}&`
              : ''
          }` +
          `${params?.query?.status ? `status=${params?.query?.status}&` : ''}`,
        method: 'GET',
        params,
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCompanyById: builder.query<any, string>({
      query: (id) => ({
        url: `/v1/user/company/${id}`, // `/user/get-active-company`,
        method: 'GET',
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getExelEmployee: builder.query<any, any>({
      query: (id) => ({
        url: `/v1/user/excel/${id}`,
        method: 'GET',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        responseHandler: async (response: any) =>
          window.URL.createObjectURL(await response.blob()),
      }),
    }),
    getThirdPartySuggestionsList: builder.query<
      ThirdPartyCompanyListData,
      string
    >({
      query: (query) => ({
        url: `/v1/third-party/suggestions?query=${query}`,
        method: 'GET',
      }),
    }),
    createCompany: builder.mutation<CompanyParams, CompanyParams>({
      query: (payload) => ({
        url: `/v1/company`,
        method: 'POST',
        body: payload,
      }),
      /*queryFn: async () => {
        return {
          error: {
            status: 409,
            data: {
              message: 'Организация с такими реквизитами уже присутствует в БД',
              error: 'AlreadyExists',
              statusCode: 409,
            },
          },
        }
      },*/
    }),
    updateCompany: builder.mutation<
      void,
      { id: number; payload: Partial<CompanyParams> }
    >({
      query: ({ id, payload }) => ({
        url: `/v1/user/company/${id}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    deleteCompanyType: builder.mutation<
      void,
      { companyId: number; companyTypeId: number }
    >({
      query: ({ companyId, companyTypeId }) => ({
        url: `/v1/user/company/${companyId}/companyType/${companyTypeId}`,
        method: 'DELETE',
      }),
    }),
    getCompanySearchList: builder.query<CompanyParamsListData, string>({
      query: (params) => ({
        url: `/v1/user/company${params ? `?short_title=` + params : ''}`,
        method: 'GET',
      }),
    }),
    getBankAccountsList: builder.query<
      BankAccountList,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { id: string; params?: any }
    >({
      query: ({ id, params }) => ({
        url: `/v1/user/company/${id}/account`,
        method: 'GET',
        params,
      }),
    }),
    getCompanyPositions: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { id: string; params?: any }
    >({
      query: ({ id, params }) => ({
        url: `/v1/user/company/${id}/positions`,
        method: 'GET',
        params,
      }),
    }),
    createCompanyPosition: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { id: number; payload: any }
    >({
      query: ({ id, payload }) => ({
        url: `/v1/user/position/${id}`,
        method: 'POST',
        body: payload,
      }),
    }),
    createBankAccount: builder.mutation<
      BankInfo,
      { companyId: number; payload: BankInfo }
    >({
      query: ({ companyId, payload }) => ({
        url: `/v1/user/company/${companyId}/account`,
        method: 'POST',
        body: payload,
      }),
    }),
    updateBankAccount: builder.mutation<
      void,
      { companyId: number; accountId: number; payload: BankInfo }
    >({
      query: ({ companyId, accountId, payload }) => ({
        url: `/v1/user/company/${companyId}/account/${accountId}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    deleteBankAccount: builder.mutation<
      void,
      { companyId: number; accountId: number }
    >({
      query: ({ companyId, accountId }) => ({
        url: `/v1/user/company/${companyId}/account/${accountId}`,
        method: 'DELETE',
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCurrentUser: builder.query<any, void>({
      query: () => ({
        url: `/v1/user/me`,
        method: 'GET',
      }),
    }),
    getEmployees: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { id: string; params?: any }
    >({
      query: ({ id, params }) => ({
        url: `/v1/user/company/${id}/employee`,
        method: 'GET',
        params,
      }),
    }),
    getEmployeeById: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      { companyId: number; employeeId: number }
    >({
      query: ({ companyId, employeeId }) => ({
        url: `/v1/user/company/${companyId}/employee/${employeeId}`,
        method: 'GET',
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    createEmployee: builder.mutation<any, { id: number; payload: any }>({
      query: ({ id, payload }) => ({
        url: `/v1/user/company/${id}/employee`,
        method: 'POST',
        body: payload,
      }),
      /*transformErrorResponse: () => {
        return {
          status: 400,
          data: {
            message: 'Пользователь с такими ФИО и ДР уже существует',
            status: 400,
          },
        }
      },*/
    }),
    updateEmployee: builder.mutation<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { companyId: any; employeeId: number; payload: any }
    >({
      query: ({ companyId, employeeId, payload }) => ({
        url: `/v1/user/company/${companyId}/employee/${employeeId}`,
        method: 'PATCH',
        body: payload,
      }),
      /*transformErrorResponse: () => {
        return {
          status: 400,
          data: {
            message: ['phone must be a valid phone number'],
            error: 'Bad Request',
            statusCode: 400,
          },
        }
      },*/
    }),
    getUser: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      void
    >({
      query: () => ({
        url: `/v1/user/me`,
        method: 'GET',
      }),
    }),
    getProducts: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      void
    >({
      query: () => ({
        url: `/v1/products`,
        method: 'GET',
      }),
    }),
    getRoles: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { companyId: number; params?: any }
    >({
      query: ({ companyId, params }) => ({
        url: `/v1/user/roles/${companyId}`,
        method: 'GET',
        params,
      }),
    }),
    changeActiveCompany: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/v1/user/company/${id}/change`,
        method: 'PATCH',
      }),
    }),
    getCompanyTypes: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      { query: string }
    >({
      query: (params) => ({
        url: `/v1/user/getCompanyTypes`,
        method: 'GET',
        params,
      }),
    }),
    getSendInvite: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { companyId: number; employeeId: number; contact_id: number }
    >({
      query: ({ companyId, employeeId, contact_id }) => ({
        url: `/v1/user/company/${companyId}/employee/${employeeId}/invite`,
        method: 'GET',
        params: { contact_id: contact_id },
      }),
    }),
    updateEmployeeDocument: builder.mutation<
      void,
      {
        companyId: number
        employeeId?: number
        documentId?: number
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload: any
      }
    >({
      query: ({ companyId, employeeId, documentId, payload }) => ({
        url: `/v1/user/company/${companyId}/employee/${employeeId}/document/${documentId}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    // // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // createFile: builder.query<any, string>({
    //   query: (url) => ({
    //     url: `${url}`,
    //     method: 'GET',
    //     responseHandler: async (response) =>
    //       window.URL.createObjectURL(await response.blob()),
    //   }),
    // }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDocumentFile: builder.query<any, string>({
      query: (url) => ({
        url: `${url}`,
        method: 'GET',
        responseHandler: async (response) =>
          window.URL.createObjectURL(await response.blob()),
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uploadEmployees: builder.mutation<any, { id: number; payload: any }>({
      query: ({ id, payload }) => ({
        url: `/v1/user/excel/upload/${id}`,
        method: 'POST',
        body: payload,
      }),
      /*transformResponse: () => {
        return {
          createdUsers: 0,
          createdEmployees: 0,
          createdStaffRoles: 6,
          createdContacts: 5,
          failedEmployees: 8,
          errors: [
            'Строка 5: ФИО пользователя, найденного по email  или номеру телефона не совпадают с ФИО из файла',
            'Строка 9: В базе есть сотрудник с такими же ФИО и ДР, но другим СНИЛС: Вергун Андрей Григорьевич, СНИЛС: 05285858089',
            'Строка 10: Пропуск строки 10 - можно указать только роль "Мед. осматриваемый"',
            'Строка 11: Пропуск строки 11 - можно указать только роль "Мед. осматриваемый"',
            'Строка 12: Пропуск строки 12 - можно указать только роль "Мед. осматриваемый"',
            'Строка 13: Пропуск строки 13 - роли не найдены',
            'Строка 15: Пропуск строки 15 - можно указать только роль "Мед. осматриваемый"',
            'Строка 16: ФИО пользователя, найденного по email  или номеру телефона не совпадают с ФИО из файла',
          ],
        }
      },*/
    }),
    getBranches: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { companyId: number; params?: any }
    >({
      query: ({ companyId, params }) => ({
        url: `/v1/user/company/${companyId}/branches`,
        method: 'GET',
        params, // parentId
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    createBranches: builder.mutation<any, { companyId: number; payload: any }>({
      query: ({ companyId, payload }) => ({
        url: `/v1/user/company/${companyId}/branches`,
        method: 'POST',
        body: payload,
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateBranches: builder.mutation<any, { companyId: number; payload: any }>({
      query: ({ companyId, payload }) => ({
        url: `/v1/user/company/${companyId}/branches`,
        method: 'PATCH',
        body: payload,
      }),
    }),
  }),
})

export const {
  useLazyGetUsersListQuery,
  useGetCompanyListQuery,
  useLazyGetCompanyListQuery,
  useLazyGetCompanyByIdQuery,
  useGetCompanyByIdQuery,
  useLazyGetExelEmployeeQuery,
  useLazyGetThirdPartySuggestionsListQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyTypeMutation,
  useGetCompanySearchListQuery,
  useLazyGetCompanySearchListQuery,
  useGetBankAccountsListQuery,
  useLazyGetBankAccountsListQuery,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useDeleteBankAccountMutation,
  useGetCurrentUserQuery,
  useGetEmployeeByIdQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useLazyGetCurrentUserQuery,
  useLazyGetEmployeeByIdQuery,
  useLazyGetEmployeesQuery,
  useGetRolesQuery,
  useLazyGetRolesQuery,
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useChangeActiveCompanyMutation,
  useGetCompanyTypesQuery,
  useLazyGetCompanyPositionsQuery,
  useCreateCompanyPositionMutation,
  useLazyGetSendInviteQuery,
  useUpdateEmployeeDocumentMutation,
  useLazyGetDocumentFileQuery,
  useUploadEmployeesMutation,
  useGetBranchesQuery,
  useLazyGetBranchesQuery,
  useCreateBranchesMutation,
  useUpdateBranchesMutation,
} = companyApi
